import { Logger } from "../../../../clients/logger";
import * as Constants from "../../../../common/constants";
import { MockDockableWindow } from "../Common/MockDockableWindow";
import { WindowPoolSingleton } from "../Common/Pools/PoolSingletons";
import { WINDOW_SERVICE_REQUESTS } from "../../../../common/constants";
import { FinsembleWindowInternal } from "../WindowAbstractions/FinsembleWindowInternal";
import { BaseWindow } from "../WindowAbstractions/BaseWindow";
import { Launcher } from "../Launcher/launcher";
import DockingMain from "../Docking/dockingMain";
import { ResponderMessage, RouterClient } from "../../../../clients/routerClient";
import { publishWrapState } from "../Common/publishWrapState";
import { removeWindowReferenceFromActiveWorkspace } from "../../workspace/workspaceAdminAPI";
import { StandardError, TabCloseStatus } from "../../../../types";
import { getStore } from "../store";
import { actions } from "../modules/actions";
import { WindowServiceStoreInstance } from "../localTypes";
import isNumber from "lodash/isNumber";
import { ApiUsageTracker } from "../../common/apiUsageTracker";

const { REMOTE_FOCUS, SET_ALWAYS_ON_TOP, IS_ALWAYS_ON_TOP } = WINDOW_SERVICE_REQUESTS;

const store: WindowServiceStoreInstance = getStore();

export class WindowPrimitives {
	dockingMain: DockingMain;

	launcher: Launcher;

	eventInterruptors: any;

	moduleReady = false;

	apiUsageTracker: ApiUsageTracker;

	constructor(dockingMain: DockingMain, launcher: Launcher) {
		this.dockingMain = dockingMain;
		this.launcher = launcher;
		this.bindAllFunctions();
		this.eventInterruptors = {};
		this.apiUsageTracker = new ApiUsageTracker();
	}

	initialize() {
		this.definePubicInterface_Window();
	}

	setReady() {
		this.moduleReady = true;
	}

	windowServiceChannelName(channelTopic) {
		return `WindowService-Request-${channelTopic}`;
	}

	bindAllFunctions() {
		for (const name of Object.getOwnPropertyNames(Object.getPrototypeOf(this))) {
			if (this[name] instanceof Function) {
				this[name] = this[name].bind(this); // only bind function properties
			}
		}
	}

	definePubicInterface_Window() {
		Logger.system.debug("definePubicInterface_Window");

		// entry points for public window functions
		RouterClient.addResponder(this.windowServiceChannelName("addEventListener"), this.addEventListenerHandler);
		RouterClient.addResponder(this.windowServiceChannelName("removeEventListener"), this.removeEventListenerHandler);
		RouterClient.addResponder(
			this.windowServiceChannelName("registerInterruptibleEvent"),
			this.registerInterruptibleEventHandler
		);
		RouterClient.addResponder(this.windowServiceChannelName("minimize"), this.minimizeHandler);
		RouterClient.addResponder(this.windowServiceChannelName("maximize"), this.maximizeHandler);
		RouterClient.addResponder(this.windowServiceChannelName("restore"), this.restoreHandler);
		RouterClient.addResponder(this.windowServiceChannelName("focus"), this.focusHandler);
		RouterClient.addResponder(this.windowServiceChannelName("blur"), this.blurHandler);
		RouterClient.addResponder(this.windowServiceChannelName("bringToFront"), this.bringToFrontHandler);
		RouterClient.addResponder(this.windowServiceChannelName("saveWindowOptions"), this.saveWindowOptionsHandler);
		RouterClient.addResponder(this.windowServiceChannelName("setIgnoreMouseEvents"), this.setIgnoreMouseEventsHandler);
		RouterClient.addResponder(this.windowServiceChannelName("showNativeOpenDialog"), this.showNativeOpenDialogHandler);
		RouterClient.addResponder(this.windowServiceChannelName("setShape"), this.setShapeHandler);
		RouterClient.addResponder(this.windowServiceChannelName("setBounds"), this.setBoundsHandler);
		RouterClient.addResponder(this.windowServiceChannelName("getBounds"), this.getBoundsHandler);
		RouterClient.addResponder(this.windowServiceChannelName("addBrowserView"), this.addBrowserViewHandler);
		RouterClient.addResponder(this.windowServiceChannelName("removeBrowserView"), this.removeBrowserViewHandler);
		RouterClient.addResponder(this.windowServiceChannelName("reload"), this.reloadHandler);
		RouterClient.addResponder(this.windowServiceChannelName("setViewBounds"), this.setViewBoundsHandler);
		RouterClient.addResponder(this.windowServiceChannelName("bringViewToFront"), this.bringViewToFrontHandler);
		RouterClient.addResponder(this.windowServiceChannelName("showBrowserView"), this.showBrowserView);
		RouterClient.addResponder(this.windowServiceChannelName("hideBrowserView"), this.hideBrowserView);
		RouterClient.addResponder(this.windowServiceChannelName("getOptions"), this.getOptionsHandler);
		RouterClient.addResponder(this.windowServiceChannelName("updateOptions"), this.updateOptionsHandler);
		RouterClient.addResponder(this.windowServiceChannelName("hide"), this.hideHandler);
		RouterClient.addResponder(this.windowServiceChannelName("show"), this.showHandler);
		RouterClient.addResponder(this.windowServiceChannelName("showAt"), this.showAtHandler);
		RouterClient.addResponder(SET_ALWAYS_ON_TOP, this.setAlwaysOnTopHandler);
		RouterClient.addResponder(IS_ALWAYS_ON_TOP, this.isAlwaysOnTopHandler);
		RouterClient.addResponder(this.windowServiceChannelName("setOpacity"), this.setOpacityHandler);
		RouterClient.addResponder(this.windowServiceChannelName("close"), this.closeHandler);
		RouterClient.addResponder(this.windowServiceChannelName("isShowing"), this.isShowingHandler);
		RouterClient.addResponder(this.windowServiceChannelName("animate"), this.animateHandler);
		RouterClient.addResponder(this.windowServiceChannelName("setComponentState"), this.setComponentStateHandler);
		RouterClient.addResponder(this.windowServiceChannelName("removeComponentState"), this.removeComponentStateHandler);
		RouterClient.addResponder(this.windowServiceChannelName("setWindowState"), this.setWindowStateHandler);
		RouterClient.addResponder(
			this.windowServiceChannelName("saveCompleteWindowState"),
			this.saveCompleteWindowStateHandler
		);
		RouterClient.addResponder(this.windowServiceChannelName("getWindowState"), this.getWindowStateHandler);
		RouterClient.addResponder(this.windowServiceChannelName("getComponentState"), this.getComponentStateHandler);
		RouterClient.addResponder(
			this.windowServiceChannelName("componentStateChanged"),
			this.componentStateChangedHandler
		);
		RouterClient.addResponder("DockingService.getMonitorForWindow", this.getMonitorForWindowHandler);
		RouterClient.addListener(REMOTE_FOCUS, this.handleRemoteFocus);
	}

	// housekeeping function used in each of the public window-wrapper handlers below
	publicWindowHandlerPreface(method, queryError, queryMessage) {
		this.moduleReady ||
			Logger.system.error("windowService window primative invoked before ready", method, queryMessage);

		let okay = true;
		const { windowIdentifier, eventName, guid } = queryMessage.data;

		if (!windowIdentifier) {
			Logger.system.error(`no windowIdentifer for ${this.windowServiceChannelName(method)} handler`);
		}

		if (queryError) {
			Logger.system.error(`${this.windowServiceChannelName(method)} addResponder failed: ${queryError}`);
			okay = false;
		} else {
			windowIdentifier.windowName = windowIdentifier.windowName || windowIdentifier.name || "unknown-name";
			windowIdentifier.name = windowIdentifier.windowName;
		}

		Logger.system.debug(`WindowService-Request.${method} for ${windowIdentifier.windowName}`, queryMessage);
		return {
			okay,
			windowIdentifier,
			eventName,
			guid,
		};
	}

	async registerInterruptibleEventHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("registerInterruptibleEvent"));
		const { windowIdentifier, eventName, guid } = this.publicWindowHandlerPreface(
			"registerInterruptibleEventHandler",
			queryError,
			queryMessage
		);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			if (wrap.wrapState === "closing" || wrap.wrapState === "closed") {
				return callback("Window is being shut down. Failed to add listener.");
			}
			if (Constants.INTERRUPTIBLE_EVENTS.includes(eventName)) {
				// keep track of all listeners
				if (!this.eventInterruptors[windowIdentifier.name]) {
					this.eventInterruptors[windowIdentifier.name] = {};
				}

				if (!this.eventInterruptors[windowIdentifier.name][eventName]) {
					this.eventInterruptors[windowIdentifier.name][eventName] = {};
				}

				this.eventInterruptors[windowIdentifier.name][eventName][guid] = {
					origin: queryMessage.header.origin,
					status: "created",
				};
				Logger.system.debug(
					"Add Interruptor",
					windowIdentifier.name,
					queryMessage.header.origin,
					guid,
					this.eventInterruptors[windowIdentifier.name][eventName][guid]
				);
			}
			callback();
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	/** DH 6/15/2019
	 * Because the OS and container keep their own
	 * seperate records of which window has focus,
	 * it's possible for the two to get out of sync.
	 * To prevent that, we send messages from Assimilation
	 * and Finsemble-DLL on every OS focus event and handle
	 * them here. For every focus event coming from a window not
	 * managed by Finsemble's container, we manually blur
	 * whatever window had focus previously.
	 *
	 * If we can figure out a different way to synchronize
	 * focus between container and OS, we can remove this ad hoc
	 * and manual handling here.
	 */
	handleRemoteFocus(queryError, queryMessage) {
		const { windowHandle } = queryMessage.data;

		// DH 6/15/2019 - These types aren't right, but the best way to
		// fix would be to type the ObjectPools, which would require significant
		// refactoring.
		const windows: any[] = Object.values(WindowPoolSingleton.getAll());

		const focused: BaseWindow = windows.find((x: { focused: boolean }) => x.focused) as BaseWindow;

		// imesner: 20220727: windowOptions was null during startup on CI
		// use padStart to standardize format of window handle between assimilation and finsemble
		const focusedWindowHandle = focused?.windowOptions?.nativeWindowHandle?.padStart(8, "0");

		if (focusedWindowHandle && focusedWindowHandle !== windowHandle) {
			focused?.eventManager?.trigger("blurred");
		}
	}

	// ///////////////////////////////////////////////////////////////////////////////////////////////////////
	// The following functions handler the public "wrapper" requests
	// ///////////////////////////////////////////////////////////////////////////////////////////////////////
	async removeEventListenerHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("removeEventListener"));
		const { windowIdentifier, eventName, guid } = this.publicWindowHandlerPreface(
			"removeEventListener",
			queryError,
			queryMessage
		);

		const callback = queryMessage.sendQueryResponse;

		if (Constants.INTERRUPTIBLE_EVENTS.includes(eventName)) {
			// keep track of all listeners
			if (!this.eventInterruptors[windowIdentifier.name]) {
				this.eventInterruptors[windowIdentifier.name] = {};
			}

			if (!this.eventInterruptors[windowIdentifier.name][eventName]) {
				this.eventInterruptors[windowIdentifier.name][eventName] = {};
			}
			if (
				this.eventInterruptors[windowIdentifier.name] &&
				this.eventInterruptors[windowIdentifier.name][eventName] &&
				this.eventInterruptors[windowIdentifier.name][eventName][guid]
			) {
				Logger.system.debug(
					"Remove Interruptor",
					windowIdentifier.name,
					this.eventInterruptors[windowIdentifier.name][eventName][guid],
					guid
				);
				delete this.eventInterruptors[windowIdentifier.name][eventName][guid];
			}
		}

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			// callback is not the handler here, it's sendQueryResponse
			// when we add an event listener, there are two parameters: event name, and handler to be invoked when the event is thrown.
			// When we're removing listeners here, we're saying 'remove the listener for this event, the handler was 'callback'.
			// That's not true. In this case, callback is queryMessage.sendResponse.
			// The function below will never invoke the callback, it will simply search amongst the event handlers for
			// event 'whatever event' for a handler that === callback. It will never find that,
			// because we never added a listener with that handler.
			wrap._removeEventListener(queryMessage.data, callback);
			callback(null);
		} else {
			Logger.system.debug(`Attempt to remove event listener for non-existent window ${windowIdentifier.name}`);
			callback(null);
		}
	}

	async addEventListenerHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("addEventListener"));
		const { windowIdentifier, eventName, guid } = this.publicWindowHandlerPreface(
			"addEventListener",
			queryError,
			queryMessage
		);
		const callback = queryMessage.sendQueryResponse;

		// Wraps try to addEventListeners as soon as they are created. So make sure the window is ready before that
		if (this.launcher.pendingWindows[windowIdentifier.name]) {
			setTimeout(() => {
				this.addEventListenerHandler(queryError, queryMessage);
			}, 100);
			return;
		}
		const wrap = WindowPoolSingleton.get(windowIdentifier.name);
		// close-complete will never fire inside of that window.

		if (wrap) {
			if (wrap.wrapState === "closing" || wrap.wrapState === "closed") {
				return callback("Window is being shut down. Failed to add listener.");
			}

			if (Constants.INTERRUPTIBLE_EVENTS.includes(eventName)) {
				// keep track of all listeners
				if (!this.eventInterruptors[windowIdentifier.name]) {
					this.eventInterruptors[windowIdentifier.name] = {};
				}

				if (!this.eventInterruptors[windowIdentifier.name][eventName]) {
					this.eventInterruptors[windowIdentifier.name][eventName] = {};
				}

				Logger.system.debug("Add Interruptor", windowIdentifier.name, guid);
				this.eventInterruptors[windowIdentifier.name][eventName][guid] = {
					origin: queryMessage.header.origin,
					status: "created",
				};
			}

			// no hander is set up here, but the event manager is notified there is a remote listener
			wrap._addEventListener(queryMessage.data);
			callback();
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	/**
	 * Given a windowIdentifier, this function will find the dockableWindow or window instance, figure out which monitor the window is on, and return
	 * it back to the caller (public FinsembleWindow).
	 *
	 * @param {*} queryError callback on error
	 * @param {*} queryMessage message data and success callback
	 * @returns Promise
	 * @memberof WindowPrimitives
	 */
	async getMonitorForWindowHandler(queryError, queryMessage) {
		this.apiUsageTracker.track("DockingService.getMonitorForWindow");
		this.moduleReady || Logger.system.error("windowService window primative invoked before ready", queryMessage);
		const { windowIdentifier } = queryMessage.data;
		const callback = queryMessage.sendQueryResponse;
		const dockableWindow = this.dockingMain.getWindow(windowIdentifier.windowName, false);
		let monitor;
		if (dockableWindow) {
			monitor = this.dockingMain.getMonitorForWindow(dockableWindow);
		} else {
			const wrap = WindowPoolSingleton.get(windowIdentifier.windowName);
			if (wrap) {
				const { data: bounds } = await wrap._getBounds();
				// See documentation in MockDockableWindow for why this is necessary.
				const mock = new MockDockableWindow({ name: wrap.name, ...bounds });
				monitor = this.dockingMain.getMonitorForWindow(mock);
			} else {
				return callback(`Could not find window for ${windowIdentifier.windowName}`, null);
			}
		}

		queryMessage.sendQueryResponse(null, monitor.toJSON());
	}

	async minimizeHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("minimize"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("minimize", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._minimize(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async maximizeHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("maximize"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("maximize", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._maximize(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async restoreHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("restore"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("restore", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._restore(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async focusHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("focus"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("focus", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._focus(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async blurHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("blur"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("blur", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._blur(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async bringToFrontHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("bringToFront"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("bringToFront", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._bringToFront(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async isShowingHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("isShowing"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("isShowing", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._isShowing(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async saveWindowOptionsHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("saveWindowOptions"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("saveWindowOptions", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._saveWindowOptions(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	setIgnoreMouseEventsHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("setIgnoreMouseEvents"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("setIgnoreMouseEvents", queryError, queryMessage);
		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		wrap._setIgnoreMouseEvents(queryMessage.data, queryMessage.sendQueryResponse);
	}

	showNativeOpenDialogHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("showNativeOpenDialog"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("showNativeOpenDialog", queryError, queryMessage);
		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		wrap._showNativeOpenDialog(queryMessage.data, queryMessage.sendQueryResponse);
	}

	setShapeHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("setShape"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("setShape", queryError, queryMessage);
		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		wrap._setShape(queryMessage.data, queryMessage.sendQueryResponse);
	}

	addBrowserViewHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("addBrowserView"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("addBrowserView", queryError, queryMessage);
		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		wrap.addBrowserView(queryMessage.data, queryMessage.sendQueryResponse);
	}

	removeBrowserViewHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("removeBrowserView"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("removeBrowserView", queryError, queryMessage);
		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		wrap.removeBrowserView(queryMessage.data, queryMessage.sendQueryResponse);
	}

	reloadHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("reload"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("reload", queryError, queryMessage);
		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		wrap.reload(queryMessage.data, queryMessage.sendQueryResponse);
	}

	bringViewToFrontHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("bringViewToFront"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("bringViewToFront", queryError, queryMessage);
		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		wrap.bringViewToFront(queryMessage.data, queryMessage.sendQueryResponse);
	}

	showBrowserView(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("showBrowserView"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("showBrowserView", queryError, queryMessage);
		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		wrap.showBrowserView(queryMessage.data, queryMessage.sendQueryResponse);
	}

	hideBrowserView(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("hideBrowserView"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("hideBrowserView", queryError, queryMessage);
		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		wrap.hideBrowserView(queryMessage.data, queryMessage.sendQueryResponse);
	}

	setViewBoundsHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("setViewBounds"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("setViewBounds", queryError, queryMessage);
		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		wrap.setViewBounds(queryMessage.data, queryMessage.sendQueryResponse);
	}

	async setBoundsHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("setBounds"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("setBounds", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		// Check the bounds for completeness
		// We need left, top, width, height
		// Here we want to make sure the bounds are complete before passing them along.
		const { bounds } = queryMessage.data;
		const ofParams = [bounds.left, bounds.top, bounds.width, bounds.height];

		// Prevents updating window bounds if any of dimensions are null or undefined (fix for FIN2090)
		if (!ofParams.every(isNumber)) {
			const err = `WebWindow.setBounds: Invalid bounds for window "${windowIdentifier?.name}". Must include left, top, width, and height as numeric values.`;
			Logger.system.warn(err, bounds);
			callback?.(err);
			return;
		}

		// Generally getWindow is used inside of docking. We want errors if we can't find a window. Here, it's being used all throughout the system, including by windows whose movement isn't managed by docking. By passing false, we will eliminate unnecessary and confusing errors for the dev-user.
		const dockableWindow = this.dockingMain.getWindow(windowIdentifier.name, false);
		if (dockableWindow) {
			dockableWindow.setBounds(queryMessage.data.bounds, callback);
		} else {
			// It may be the case that the window is a native window which has not yet fully spawned; FinsembleWindowInternal.getInstance() will await the spawning window
			const wrap =
				WindowPoolSingleton.get(windowIdentifier.name) ||
				(
					await FinsembleWindowInternal.getInstance({
						name: queryMessage.data.windowIdentifier.name,
					})
				)?.wrap;
			if (wrap) {
				wrap._setBounds(queryMessage.data, callback);
			} else {
				callback(`no dockable window for "${windowIdentifier?.name}" in "setBounds"`, null);
			}
		}
	}

	async animateHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("animate"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("animate", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;
		// Generally getWindow is used inside of docking. We want errors if we can't find a window. Here, it's being used all throughout the system, including by windows whose movement isn't managed by docking. By passing false, we will eliminate unnecessary and confusing errors for the dev-user.
		const dockableWindow = this.dockingMain.getWindow(windowIdentifier.name, false);
		if (dockableWindow) {
			dockableWindow.animate(queryMessage.data, callback);
		} else {
			const wrap = WindowPoolSingleton.get(windowIdentifier.name);
			if (wrap) {
				wrap._animate(queryMessage.data, callback);
			} else {
				callback(`no dockable window for "${windowIdentifier?.name}" in "animate"`, null);
			}
		}
	}

	async getBoundsHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("getBounds"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("getBounds", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const dockableWindow = this.dockingMain.getWindow(windowIdentifier.name, false);
		if (dockableWindow) {
			const bounds = dockableWindow.getBounds();
			callback(null, bounds);
		} else {
			const wrap = WindowPoolSingleton.get(windowIdentifier.name);
			if (wrap) {
				wrap._getBounds(queryMessage.data, (err, data) => {
					Logger.system.debug("WindowService-Request.getBounds response", queryMessage.data, data);
					callback(err, data);
				});
			} else {
				callback(`unidentified window name: ${windowIdentifier.name}`, null);
			}
		}
	}

	async getOptionsHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("getOptions"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("getOptions", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._getOptions(queryMessage.data, callback);
		} else {
			Logger.system.debug("getOptionsHandler error. No wrap found for window", windowIdentifier.name);
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async updateOptionsHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("updateOptions"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("updateOptions", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._updateOptions(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async hideHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("hide"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("hide", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._hide(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async showHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("show"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("show", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._show(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async showAtHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("showAt"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("showAt", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._showAt(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async setAlwaysOnTopHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(SET_ALWAYS_ON_TOP);
		let { windowIdentifier } = this.publicWindowHandlerPreface("alwaysOnTop", queryError, queryMessage);
		let callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._alwaysOnTop(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async isAlwaysOnTopHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(IS_ALWAYS_ON_TOP);
		let { windowIdentifier } = this.publicWindowHandlerPreface("isAlwaysOnTop", queryError, queryMessage);
		let callback = queryMessage.sendQueryResponse;

		let wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._isAlwaysOnTop(null, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async setOpacityHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("setOpacity"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("setOpacity", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap._setOpacity(queryMessage.data, callback);
			wrap.windowOptions.opacity = queryMessage.data?.opacity || 1;
			wrap._saveWindowOptions();
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async setComponentStateHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("setComponentState"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("setComponentState", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap.setComponentState(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async removeComponentStateHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("removeComponentState"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("removeComponentState", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap.removeComponentState(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async setWindowStateHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("setWindowState"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("setWindowState", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap.setWindowState(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async saveCompleteWindowStateHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("saveCompleteWindowState"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("saveCompleteWindowState", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap.saveCompleteWindowState(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async getWindowStateHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("getWindowState"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("getWindowState", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap.getWindowState(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async getComponentStateHandler(queryError, queryMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("getComponentState"));
		const { windowIdentifier } = this.publicWindowHandlerPreface("getComponentState", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			wrap.getComponentState(queryMessage.data, callback);
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async componentStateChangedHandler(queryError, queryMessage) {
		const { windowIdentifier } = this.publicWindowHandlerPreface("componentStateChanged", queryError, queryMessage);
		const callback = queryMessage.sendQueryResponse;

		const wrap = WindowPoolSingleton.get(windowIdentifier.name);

		if (wrap) {
			await wrap.eventManager.trigger("component-state-changed", queryMessage.data.componentState);
			callback();
		} else {
			callback(`unidentified window name: ${windowIdentifier.name}`, null);
		}
	}

	async closeHandler(queryError: StandardError, queryMessage?: ResponderMessage) {
		this.apiUsageTracker.track(this.windowServiceChannelName("close"));
		if (!this.moduleReady) Logger.system.error("windowService window primitive invoked before ready", queryMessage);

		const { windowIdentifier } = this.publicWindowHandlerPreface("close", queryError, queryMessage);

		if (queryError || !queryMessage) {
			Logger.system.debug("WINDOW LIFECYCLE: CLOSE: closeHandler() error", queryError);
			return;
		}

		const spawnCompleted = WindowPoolSingleton.get(windowIdentifier.name);
		const spawnPending = this.launcher.pendingWindows[windowIdentifier.name];
		if (!spawnCompleted && !spawnPending) {
			Logger.system.debug("WINDOW LIFECYCLE: CLOSE: Window does not exist. Skipping.");
			queryMessage.sendQueryResponse("Window does not exist", null);
			return;
		} else if (spawnPending) {
			Logger.system.debug("WINDOW LIFECYCLE: CLOSE: Window is pending. Will wait for window to be ready.");
		}

		const { wrap } = await FinsembleWindowInternal.getInstance({
			name: windowIdentifier.name,
		});
		if (!wrap) {
			Logger.system.error(
				`windowPrimitivesEntryPoints: no wrap returned from getInstance() for ${windowIdentifier.name}`
			);
			return;
		}

		/**
		 * When we create a window, we create a wrap before the window is ready.
		 * When we retrieve a wrap, even if waitForReady is true, getInstance
		 * will return a cached wrap to prevent multiple instances of the
		 * finsembleWindow.
		 *
		 * We do not want to try to close a window that is not ready, as that could cause catastrophic errors in FEA.
		 * So, if the window is already in a closing state, the wrap will never become ready.
		 * Not doing this causes system-closed events like Alt-F4, Taskbar etc. to cause stacks to go into a bad state.
		 */
		if (wrap.wrapState === "closing" || wrap.wrapState === "closed") {
			queryMessage.sendQueryResponse("Window already in a closing state", null);
			return;
		}

		await wrap.onReady();

		Logger.system.debug("WRAP CLOSE. starting in wrap", windowIdentifier.name);

		// we update wrapState over the router, but it's not always happening fast enough to prevent new listeners from being set up
		const oldWrapState = wrap.wrapState;
		wrap.wrapState = "closing";

		publishWrapState(windowIdentifier.name, "closing");

		const state = store.getState();
		const tabCollectionId = state?.windowTitleBar?.windows?.[wrap.windowName]?.tabCollectionId;

		// The function checks closing status of all windows in the same tab collection.
		// If closing process for any window has been aborted returns `true` to abort closing for current window.
		const waitAllWindowsInTabCollection = new Promise<boolean>((resolve) => {
			if (!tabCollectionId) {
				resolve(false);
				return;
			}

			const waitInterval = setInterval(() => {
				const tabs = store.getState()?.windowTitleBar?.closingTabs?.[tabCollectionId];
				// all windows completed closing
				if (!tabs || Object.values(tabs).every((status) => status === TabCloseStatus.closingCompleted)) {
					clearInterval(waitInterval);
					resolve(false);
				}
				// if any window aborted closing
				else if (Object.values(tabs).some((status) => status === TabCloseStatus.closingAborted)) {
					clearInterval(waitInterval);
					resolve(true);
				}
			}, 100);
		});

		// saves the current window status
		const updateTabStatus = (status: TabCloseStatus) => {
			if (tabCollectionId) {
				store.dispatch(
					actions.setClosingTabStatus({
						tabCollectionId: tabCollectionId,
						windowName: wrap.windowName,
						status: status,
					})
				);
			}
		};

		// After all listeners are set up, we trigger the event <- allow other windows to prevent closing
		try {
			await wrap.eventManager.trigger("close-requested");
			updateTabStatus(TabCloseStatus.closingCompleted);
			// waits for all windows in tab collection
			const shouldAbort = await waitAllWindowsInTabCollection;

			if (shouldAbort) {
				Logger.system.log(
					"WRAP CLOSE. Some window in tab collection with `close-requested` event listener canceled the closing for",
					windowIdentifier.name
				);
				throw "event was canceled";
			}
		} catch (err) {
			if (err !== "event was canceled") return;
			updateTabStatus(TabCloseStatus.closingAborted);
			wrap.wrapState = oldWrapState;

			Logger.system.log("WRAP CLOSE. Close was canceled for", windowIdentifier.name);
			queryMessage.sendQueryResponse("Close was canceled", null);
			return;
		} finally {
			if (tabCollectionId) {
				// cleanup the data in the store
				store.dispatch(actions.removeClosingTabs(tabCollectionId));
			}
		}

		// Since we don't even call close on the window until all close-requested handlers are done, this allows for things inside the window itself to prevent closing.
		// private wraps can listen for this event, trigger a close in the window and put a kibash on the close if needed.
		// Currently only used by .NET components.
		await wrap.eventManager.trigger("_container-close-handlers");

		// This is an event for the wraps to delete themselves
		Logger.system.debug("WRAP CLOSE. Triggered close for", windowIdentifier.name);
		await wrap.eventManager.trigger("closed", queryMessage.data);

		Logger.system.debug("WRAP CLOSE. All closed events fired", windowIdentifier.name);

		// Actually close the window
		if (queryMessage.data.removeFromWorkspace) {
			await removeWindowReferenceFromActiveWorkspace({ name: wrap.name });
		}

		await wrap._close(queryMessage.data);
		Logger.system.debug("WRAP CLOSE. Removing wrap.", windowIdentifier.name);
		wrap.handleWrapRemoveRequest();
		Logger.system.debug("WRAP CLOSED. Invoking callback.", windowIdentifier.name);
		// Public wraps wait for this to clean themselves up. Can't wait on closed because our cleanup removes the wrap entirely. Other listeners might fail if our cleanup handler fires first.
		await wrap.eventManager.trigger("close-complete");

		// If we are closing a launchGroup component and we should close other components, close them.
		if (wrap.windowDescriptor?.launchGroupComponentData) {
			this._closeLaunchGroupComponentWindows(wrap.windowDescriptor?.launchGroupComponentData);
		}

		publishWrapState(windowIdentifier.name, "closed");

		WindowPoolSingleton.remove(windowIdentifier.name);
		Logger.unregisterClient({
			clientName: windowIdentifier.name,
			deleteFromPersistence: queryMessage.data.userInitiated,
		});
		queryMessage.sendQueryResponse(null, null);
	}

	/**
	 * For a given launchGroup component data, checks which other windows, as part of the launchGroup component, must also be closed.
	 *
	 * @param launchGroupData The launchGroup component data from the closing window.
	 */
	_closeLaunchGroupComponentWindows(launchGroupData) {
		Object.values(WindowPoolSingleton.getAll())
			.filter((window: any) => {
				if (window.windowDescriptor?.launchGroupComponentData) {
					const { launchGroupComponentUUID, closeComponentsTogether } =
						window.windowDescriptor?.launchGroupComponentData;
					if (closeComponentsTogether) {
						return launchGroupComponentUUID === launchGroupData.launchGroupComponentUUID;
					}
				}
				return false;
			})
			.forEach((window: any) => {
				if (window.wrapState !== "closing") {
					window.close({ removeFromWorkspace: true, closeWindow: true });
				}
			});
	}
}
